import React, { useState, useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import {
  LoadingView,
  NotificationContext,
  Button,
} from "@solsys/solsys-reactjs";
import { useContext } from "react";
import { FaFileCsv } from "react-icons/fa";
import {
  TbChartBar,
  TbChartPie,
  TbCalendarDue,
  TbAlertTriangle,
  TbCheck,
  TbCrane,
  TbSettings,
  TbFilter,
  TbChartDots,
} from "react-icons/tb";

const inspectionIntervalsQuery = gql`
  query managementInspectionIntervals($crane_inspection_interval_list_request: crane_inspection_interval_list_request) {
    crane_inspection_interval(crane_inspection_interval_list_request: $crane_inspection_interval_list_request) {
      edges {
        id
        crane_id
        service_definition_id
        next_inspection_date
        last_inspection_date
        service_definition {
          id
          name
        }
        crane {
          id
          name
        }
      }
      totalCount
    }
  }
`;

const todayStart = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString();
};

function BarChart({ data, maxCount }) {
  const total = data.reduce((sum, d) => sum + d.count, 0);
  const scale = total > 0 && maxCount > 0 ? maxCount : 1;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", height: 120, marginTop: 12 }}>
      {data.map(({ label, count, color }) => (
        <div key={label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div
            style={{
              width: "100%",
              height: Math.max(4, (count / scale) * 100),
              backgroundColor: color,
              borderRadius: "4px 4px 0 0",
              minHeight: count > 0 ? 24 : 4,
            }}
            title={`${label}: ${count}`}
          />
          <div style={{ fontSize: 18, fontWeight: 600, marginTop: 6 }}>{count}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

function PieChart({ data, size = 200 }) {
  const total = data.reduce((sum, d) => sum + d.count, 0);
  if (total === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-subtle)", fontSize: 14 }}>
        No data
      </div>
    );
  }
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 8;
  const innerRadius = radius * 0.55;
  let cumulative = 0;
  const segments = data.map(({ label, count, color }) => {
    const pct = count / total;
    const startAngle = (cumulative / total) * 360;
    cumulative += count;
    const endAngle = (cumulative / total) * 360;
    const rad = (deg) => (deg * Math.PI) / 180;
    const x1 = cx + radius * Math.cos(rad(startAngle - 90));
    const y1 = cy + radius * Math.sin(rad(startAngle - 90));
    const x2 = cx + radius * Math.cos(rad(endAngle - 90));
    const y2 = cy + radius * Math.sin(rad(endAngle - 90));
    const ix1 = cx + innerRadius * Math.cos(rad(startAngle - 90));
    const iy1 = cy + innerRadius * Math.sin(rad(startAngle - 90));
    const ix2 = cx + innerRadius * Math.cos(rad(endAngle - 90));
    const iy2 = cy + innerRadius * Math.sin(rad(endAngle - 90));
    const large = endAngle - startAngle > 180 ? 1 : 0;
    const d = `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${large} 0 ${ix1} ${iy1} Z`;
    return { d, color, label, count, pct: ((pct * 100) | 0) };
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap", marginTop: 12 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((seg, i) => (
          <path key={seg.label} d={seg.d} fill={seg.color} stroke="var(--card-bg)" strokeWidth={1.5} title={`${seg.label}: ${seg.count} (${seg.pct}%)`} />
        ))}
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data.map(({ label, count, color }) => {
          const pct = total > 0 ? ((count / total) * 100) | 0 : 0;
          return (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: color }} />
              <span style={{ fontWeight: 600 }}>{count}</span>
              <span style={{ color: "var(--text-muted)" }}>{label}</span>
              <span style={{ color: "var(--text-subtle)", fontSize: 12 }}>({pct}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DueOverTimeChart({ edges, dueSoonDays, today }) {
  const dueSoonEnd = addDays(today, dueSoonDays);
  const buckets = useMemo(() => {
    const byWeek = [];
    for (let i = -3; i <= Math.ceil(dueSoonDays / 7) + 1; i++) {
      const weekStart = addDays(today, i * 7);
      const weekEnd = addDays(weekStart, 7);
      byWeek.push({
        label: formatDate(weekStart).slice(0, 5) + "–" + formatDate(addDays(weekEnd, -1)).slice(0, 5),
        weekStart: weekStart.getTime(),
        weekEnd: weekEnd.getTime(),
        overdue: 0,
        dueSoon: 0,
        onTrack: 0,
      });
    }
    edges.forEach((e) => {
      const nextStr = e.next_inspection_date;
      if (!nextStr) return;
      const next = new Date(nextStr);
      next.setHours(0, 0, 0, 0);
      const t = next.getTime();
      for (const bucket of byWeek) {
        if (t >= bucket.weekStart && t < bucket.weekEnd) {
          if (next < today) bucket.overdue++;
          else if (next <= dueSoonEnd) bucket.dueSoon++;
          else bucket.onTrack++;
          break;
        }
      }
    });
    return byWeek.slice(-12);
  }, [edges, dueSoonDays, today]);

  const maxVal = Math.max(1, ...buckets.map((b) => b.overdue + b.dueSoon + b.onTrack));
  return (
    <div style={{ marginTop: 12, overflowX: "auto" }}>
      <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 140, minWidth: 400 }}>
        {buckets.map((b, idx) => (
          <div key={idx} style={{ flex: 1, minWidth: 48, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column-reverse", height: 100, width: "80%" }}>
              {b.onTrack > 0 && (
                <div style={{ height: `${(b.onTrack / maxVal) * 100}%`, backgroundColor: "#28a745", borderRadius: "2px 2px 0 0", minHeight: 2 }} title={`On track: ${b.onTrack}`} />
              )}
              {b.dueSoon > 0 && (
                <div style={{ height: `${(b.dueSoon / maxVal) * 100}%`, backgroundColor: "#fd7e14", minHeight: 2 }} title={`Due soon: ${b.dueSoon}`} />
              )}
              {b.overdue > 0 && (
                <div style={{ height: `${(b.overdue / maxVal) * 100}%`, backgroundColor: "#dc3545", borderRadius: "0 0 2px 2px", minHeight: 2 }} title={`Overdue: ${b.overdue}`} />
              )}
            </div>
            <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4, transform: "rotate(-45deg)", whiteSpace: "nowrap" }}>{b.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 12, color: "var(--text-muted)" }}>
        <span><span style={{ display: "inline-block", width: 10, height: 10, backgroundColor: "#dc3545", marginRight: 4 }} /> Overdue</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, backgroundColor: "#fd7e14", marginRight: 4 }} /> Due soon</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, backgroundColor: "#28a745", marginRight: 4 }} /> On track</span>
      </div>
    </div>
  );
}

function exportTableToCSV(rows, columns, filename) {
  const headers = columns.map((c) => c.label).join(",");
  const escape = (v) => (v == null ? "" : String(v).replace(/"/g, '""'));
  const rowLines = rows.map((row) => columns.map((c) => `"${escape(c.render ? c.render(row[c.key], row) : row[c.key])}"`).join(","));
  const csv = [headers, ...rowLines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename || "report.csv";
  link.click();
  URL.revokeObjectURL(link.href);
}

function BreakdownTable({ rows, columns, onRowClick }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: "2px solid var(--card-border)", background: "var(--table-header-bg)" }}>
            {columns.map((col) => (
              <th key={col.key} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={row.id || idx}
              style={{
                borderBottom: "1px solid var(--table-row-border)",
                cursor: onRowClick ? "pointer" : "default",
              }}
              onClick={() => onRowClick && onRowClick(row)}
            >
              {columns.map((col) => (
                <td key={col.key} style={{ padding: "10px 12px" }}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SECTION_KEYS = {
  summary: "summary",
  statusChart: "statusChart",
  dueOverTime: "dueOverTime",
  breakdown: "breakdown",
  overdueList: "overdueList",
};

export default function ManagementDashboard() {
  const history = useHistory();
  const { addNotification } = useContext(NotificationContext);
  const [dueSoonDays, setDueSoonDays] = useState(30);
  const [groupBy, setGroupBy] = useState("service_definition");
  const [chartType, setChartType] = useState("bar");
  const [visibleSections, setVisibleSections] = useState({
    [SECTION_KEYS.summary]: true,
    [SECTION_KEYS.statusChart]: true,
    [SECTION_KEYS.dueOverTime]: true,
    [SECTION_KEYS.breakdown]: true,
    [SECTION_KEYS.overdueList]: true,
  });

  const today = todayStart();
  const dueSoonEnd = addDays(today, dueSoonDays);

  const toggleSection = (key) => {
    setVisibleSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const { loading, data, error } = useQuery(inspectionIntervalsQuery, {
    variables: {
      crane_inspection_interval_list_request: {
        pagination: { limit: 10000 },
        filters: [],
        sorting: [{ field: "next_inspection_date", direction: "ASC" }],
      },
    },
    fetchPolicy: "network-only",
    onError: (err) => {
      console.error("Error fetching inspection intervals:", err);
      addNotification("bad", "Failed to load inspection data");
    },
  });

  if (loading) return <LoadingView />;
  if (error) {
    return (
      <div style={{ padding: 20, color: "#c00" }}>
        Failed to load inspection data. Please try again or contact support.
      </div>
    );
  }

  const edges = data?.crane_inspection_interval?.edges ?? [];
  const totalCount = data?.crane_inspection_interval?.totalCount ?? 0;

  const classify = (nextDateStr) => {
    if (!nextDateStr) return "on_track";
    const next = new Date(nextDateStr);
    next.setHours(0, 0, 0, 0);
    if (next < today) return "overdue";
    if (next <= dueSoonEnd) return "due_soon";
    return "on_track";
  };

  const overdue = edges.filter((e) => classify(e.next_inspection_date) === "overdue");
  const dueSoon = edges.filter((e) => classify(e.next_inspection_date) === "due_soon");
  const onTrack = edges.filter((e) => classify(e.next_inspection_date) === "on_track");

  const statusChartData = [
    { label: "Overdue", count: overdue.length, color: "#dc3545" },
    { label: "Due Soon", count: dueSoon.length, color: "#fd7e14" },
    { label: "On Track", count: onTrack.length, color: "#28a745" },
  ];
  const maxStatusCount = Math.max(1, ...statusChartData.map((d) => d.count));
  const totalClassified = overdue.length + dueSoon.length + onTrack.length;
  const complianceRatePct = totalClassified > 0 ? ((onTrack.length / totalClassified) * 100).toFixed(1) : "—";
  const atRiskPct = totalClassified > 0 ? (((overdue.length + dueSoon.length) / totalClassified) * 100).toFixed(1) : "—";

  const byServiceDefinition = {};
  edges.forEach((e) => {
    const key = e.service_definition?.name || `Definition ${e.service_definition_id}`;
    if (!byServiceDefinition[key]) byServiceDefinition[key] = { overdue: 0, dueSoon: 0, onTrack: 0, id: e.service_definition_id };
    const status = classify(e.next_inspection_date);
    if (status === "overdue") byServiceDefinition[key].overdue++;
    else if (status === "due_soon") byServiceDefinition[key].dueSoon++;
    else byServiceDefinition[key].onTrack++;
  });
  const serviceDefinitionRows = Object.entries(byServiceDefinition).map(([name, counts]) => ({
    name,
    id: counts.id,
    overdue: counts.overdue,
    dueSoon: counts.dueSoon,
    onTrack: counts.onTrack,
    total: counts.overdue + counts.dueSoon + counts.onTrack,
  })).sort((a, b) => b.overdue - a.overdue || b.dueSoon - a.dueSoon);

  const byCrane = {};
  edges.forEach((e) => {
    const key = e.crane?.name || `Crane ${e.crane_id}`;
    if (!byCrane[key]) byCrane[key] = { crane_id: e.crane_id, overdue: 0, dueSoon: 0, onTrack: 0 };
    const status = classify(e.next_inspection_date);
    if (status === "overdue") byCrane[key].overdue++;
    else if (status === "due_soon") byCrane[key].dueSoon++;
    else byCrane[key].onTrack++;
  });
  const craneRows = Object.entries(byCrane).map(([name, counts]) => ({
    name,
    crane_id: counts.crane_id,
    overdue: counts.overdue,
    dueSoon: counts.dueSoon,
    onTrack: counts.onTrack,
    total: counts.overdue + counts.dueSoon + counts.onTrack,
  })).sort((a, b) => b.overdue - a.overdue || b.dueSoon - a.dueSoon);

  const cardStyle = {
    background: "var(--card-bg)",
    color: "var(--text-color)",
    border: "1px solid var(--card-border)",
    borderRadius: 8,
    padding: 20,
    boxShadow: "var(--shadow-card)",
    cursor: "pointer",
  };
  const sectionStyle = {
    background: "var(--card-bg)",
    color: "var(--text-color)",
    border: "1px solid var(--card-border)",
    borderRadius: 8,
    padding: 20,
    boxShadow: "var(--shadow-card)",
    marginBottom: 20,
  };

  return (
    <div style={{ padding: 20, minHeight: "calc(100vh - 40px)", background: "var(--app-bg)", color: "var(--text-color)" }}>
      <h1 style={{ marginBottom: 8, fontSize: 24, color: "var(--text-color)" }}>Management Dashboard — Inspection Data</h1>
      <p style={{ marginBottom: 24, color: "var(--text-muted)", fontSize: 14 }}>
        Graphs and aggregated insights from collected inspection data. Customize reports and visualizations below.
      </p>

      <div style={{ marginBottom: 20, display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <TbFilter size={18} style={{ color: "var(--text-muted)" }} />
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14 }}>Due soon within:</span>
            <select
              value={dueSoonDays}
              onChange={(e) => setDueSoonDays(Number(e.target.value))}
              style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--input-border)" }}
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14 }}>Group by:</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--input-border)" }}
            >
              <option value="service_definition">Service Definition</option>
              <option value="crane">Crane</option>
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14 }}>Status chart:</span>
            <select
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--input-border)" }}
            >
              <option value="bar">Bar</option>
              <option value="pie">Pie</option>
            </select>
          </label>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginLeft: "auto" }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Show:</span>
          {[
            [SECTION_KEYS.summary, "Summary"],
            [SECTION_KEYS.statusChart, "Status chart"],
            [SECTION_KEYS.dueOverTime, "Due over time"],
            [SECTION_KEYS.breakdown, "Breakdown"],
            [SECTION_KEYS.overdueList, "Overdue list"],
          ].map(([id, label]) => (
            <label key={id} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={visibleSections[id]} onChange={() => toggleSection(id)} />
              {label}
            </label>
          ))}
        </div>
      </div>

      {visibleSections[SECTION_KEYS.summary] && (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
        <div style={{ ...cardStyle, borderLeft: "4px solid var(--link-color)" }} onClick={() => history.push("/ecrane/list")}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, color: "var(--text-muted)", fontSize: 13 }}>
            <TbChartBar size={18} />
            <span>Total intervals</span>
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: "var(--text-color)" }}>{totalCount}</div>
          <div style={{ fontSize: 11, color: "var(--text-subtle)", marginTop: 2 }}>Inspection schedules</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: "4px solid #dc3545" }} onClick={() => history.push("/ecrane/list")}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, color: "var(--text-muted)", fontSize: 13 }}>
            <TbAlertTriangle size={18} style={{ color: "#dc3545" }} />
            <span>Overdue</span>
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#dc3545" }}>{overdue.length}</div>
          <div style={{ fontSize: 11, color: "var(--text-subtle)", marginTop: 2 }}>Past due</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: "4px solid #fd7e14" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, color: "var(--text-muted)", fontSize: 13 }}>
            <TbCalendarDue size={18} style={{ color: "#fd7e14" }} />
            <span>Due soon</span>
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#fd7e14" }}>{dueSoon.length}</div>
          <div style={{ fontSize: 11, color: "var(--text-subtle)", marginTop: 2 }}>Within {dueSoonDays} days</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: "4px solid #28a745" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, color: "var(--text-muted)", fontSize: 13 }}>
            <TbCheck size={18} style={{ color: "#28a745" }} />
            <span>On track</span>
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#28a745" }}>{onTrack.length}</div>
          <div style={{ fontSize: 11, color: "var(--text-subtle)", marginTop: 2 }}>No action yet</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: "4px solid #0d6efd" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, color: "var(--text-muted)", fontSize: 13 }}>
            <TbChartDots size={18} style={{ color: "#0d6efd" }} />
            <span>Compliance</span>
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#0d6efd" }}>{complianceRatePct}%</div>
          <div style={{ fontSize: 11, color: "var(--text-subtle)", marginTop: 2 }}>On track vs total</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: "4px solid #6f42c1" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, color: "var(--text-muted)", fontSize: 13 }}>
            <TbAlertTriangle size={18} style={{ color: "#6f42c1" }} />
            <span>At risk</span>
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#6f42c1" }}>{atRiskPct}%</div>
          <div style={{ fontSize: 11, color: "var(--text-subtle)", marginTop: 2 }}>Overdue + due soon</div>
        </div>
      </div>
      )}

      {visibleSections[SECTION_KEYS.statusChart] && (
      <div style={sectionStyle}>
        <h2 style={{ marginBottom: 8, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
          {chartType === "pie" ? <TbChartPie size={22} /> : <TbChartBar size={22} />}
          Inspection Status Overview
        </h2>
        {chartType === "pie" ? (
          <PieChart data={statusChartData} size={220} />
        ) : (
          <BarChart data={statusChartData} maxCount={maxStatusCount} />
        )}
      </div>
      )}

      {visibleSections[SECTION_KEYS.dueOverTime] && (
      <div style={sectionStyle}>
        <h2 style={{ marginBottom: 8, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
          <TbCalendarDue size={22} />
          Inspections Due by Week
        </h2>
        <DueOverTimeChart edges={edges} dueSoonDays={dueSoonDays} today={today} />
      </div>
      )}

      {visibleSections[SECTION_KEYS.breakdown] && (
      <div style={sectionStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
            {groupBy === "crane" ? <TbCrane size={22} /> : <TbSettings size={22} />}
            {groupBy === "crane" ? "By Crane" : "By Service Definition"}
          </h2>
          <div className="page-header-options">
            <Button
              onClick={() => {
                const rows = groupBy === "crane" ? craneRows : serviceDefinitionRows;
                const columns = groupBy === "crane"
                  ? [{ key: "name", label: "Crane" }, { key: "overdue", label: "Overdue" }, { key: "dueSoon", label: "Due Soon" }, { key: "onTrack", label: "On Track" }, { key: "total", label: "Total" }]
                  : [{ key: "name", label: "Service Definition" }, { key: "overdue", label: "Overdue" }, { key: "dueSoon", label: "Due Soon" }, { key: "onTrack", label: "On Track" }, { key: "total", label: "Total" }];
                exportTableToCSV(rows, columns, `inspection-breakdown-${groupBy}-${formatDate(today)}.csv`);
              }}
            >
              <FaFileCsv />
            </Button>
          </div>
        </div>
        {groupBy === "crane" ? (
          <BreakdownTable
            rows={craneRows}
            columns={[
              { key: "name", label: "Crane" },
              { key: "overdue", label: "Overdue" },
              { key: "dueSoon", label: "Due Soon" },
              { key: "onTrack", label: "On Track" },
              { key: "total", label: "Total" },
            ]}
            onRowClick={(row) => row.crane_id && history.push(`/ecrane/edit/${row.crane_id}`)}
          />
        ) : (
          <BreakdownTable
            rows={serviceDefinitionRows}
            columns={[
              { key: "name", label: "Service Definition" },
              { key: "overdue", label: "Overdue" },
              { key: "dueSoon", label: "Due Soon" },
              { key: "onTrack", label: "On Track" },
              { key: "total", label: "Total" },
            ]}
          />
        )}
      </div>
      )}

      {visibleSections[SECTION_KEYS.overdueList] && overdue.length > 0 && (
        <div style={sectionStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 18, color: "#dc3545" }}>Overdue Inspections (next {overdue.length})</h2>
            <div className="page-header-options">
              <Button
                onClick={() => {
                  const rows = overdue.slice(0, 500).map((e) => ({
                    id: e.id,
                    crane: e.crane?.name || `Crane ${e.crane_id}`,
                    service_definition: e.service_definition?.name || "—",
                    next_inspection_date: formatDate(e.next_inspection_date),
                    crane_id: e.crane_id,
                  }));
                  const columns = [
                    { key: "crane", label: "Crane" },
                    { key: "service_definition", label: "Service Definition" },
                    { key: "next_inspection_date", label: "Was Due" },
                  ];
                  exportTableToCSV(rows, columns, `overdue-inspections-${formatDate(today)}.csv`);
                }}
              >
                <FaFileCsv />
              </Button>
            </div>
          </div>
          <BreakdownTable
            rows={overdue.slice(0, 20).map((e) => ({
              id: e.id,
              crane: e.crane?.name || `Crane ${e.crane_id}`,
              service_definition: e.service_definition?.name || "—",
              next_inspection_date: formatDate(e.next_inspection_date),
              crane_id: e.crane_id,
            }))}
            columns={[
              { key: "crane", label: "Crane" },
              { key: "service_definition", label: "Service Definition" },
              { key: "next_inspection_date", label: "Was Due" },
            ]}
            onRowClick={(row) => row.crane_id && history.push(`/ecrane/edit/${row.crane_id}`)}
          />
          {overdue.length > 20 && (
            <div style={{ marginTop: 8, fontSize: 14, color: "var(--text-muted)" }}>
              Showing 20 of {overdue.length} overdue. Export CSV for full list.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
